/**
 * BatchImageMode V2
 *
 * Haupt-Komponente fur die Batch-Bildgenerierung mit Gemini Batch API.
 *
 * Layout V2:
 * - Tabs: "Erstellen" | "Verlauf"
 * - Erstellen: Full-Width BatchGenerationFormV2
 * - Verlauf: BatchRunsList + BatchRunDetails
 *
 * @accessibility WCAG 2.1 AA + BFSG konform
 */

import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Package, Plus, History, AlertCircle, Loader2 } from 'lucide-react';
import { getBatchConfig } from '@/lib/api/batch';
import { Alert } from '@/components/v2/ui';
import {
  BatchGenerationFormV2,
  BatchRunsList,
  BatchRunDetails,
} from '@/components/v2/tools/batch';
import { cn } from '@/lib/utils';

type Tab = 'create' | 'history';

export function BatchImageMode() {
  const [activeTab, setActiveTab] = useState<Tab>('create');
  const [selectedRunId, setSelectedRunId] = useState<string>();

  const { data: config, isLoading: configLoading } = useQuery({
    queryKey: ['batch', 'config'],
    queryFn: getBatchConfig,
  });

  const handleSelectRun = useCallback((runId: string) => {
    setSelectedRunId((prev) => (prev === runId ? undefined : runId));
  }, []);

  const handleSuccess = useCallback(() => {
    setActiveTab('history');
  }, []);

  // Loading State: Config wird noch geladen
  if (configLoading || config === undefined) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  // Config geladen, aber Batch-Modus deaktiviert
  if (!config.enabled) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <Alert variant="warning" className="max-w-md">
          <AlertCircle className="h-5 w-5" />
          <div>
            <p className="font-medium">Batch-Modus deaktiviert</p>
            <p className="text-sm mt-1">
              Der Batch-Modus ist für diesen Workspace nicht verfügbar.
            </p>
          </div>
        </Alert>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Tabs Header */}
      <div className="flex-shrink-0 border-b border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900">
        <div className="flex items-center gap-1 px-6 pt-4">
          <TabButton
            active={activeTab === 'create'}
            onClick={() => setActiveTab('create')}
            icon={<Plus className="h-4 w-4" />}
            label="Erstellen"
          />
          <TabButton
            active={activeTab === 'history'}
            onClick={() => setActiveTab('history')}
            icon={<History className="h-4 w-4" />}
            label="Verlauf"
          />
        </div>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'create' ? (
          <div className="p-6">
            <BatchGenerationFormV2 onSuccess={handleSuccess} />
          </div>
        ) : (
          <div className="flex h-full">
            {/* Runs List */}
            <section className="flex-1 overflow-y-auto p-6 border-r border-neutral-200 dark:border-neutral-700">
              <div className="flex items-center gap-2 mb-6">
                <Package className="h-5 w-5 text-orange-500" />
                <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                  Batch-Jobs
                </h2>
              </div>
              <BatchRunsList
                selectedRunId={selectedRunId}
                onSelectRun={handleSelectRun}
              />
            </section>

            {/* Details Panel */}
            <aside className="w-[450px] flex-shrink-0 overflow-y-auto p-6 bg-neutral-50 dark:bg-neutral-900/50">
              {selectedRunId ? (
                <BatchRunDetails runId={selectedRunId} />
              ) : (
                <div className="flex flex-col items-center justify-center h-64 text-neutral-500">
                  <Package className="h-12 w-12 mb-3 opacity-50" />
                  <p>Wahle einen Batch-Job aus der Liste</p>
                </div>
              )}
            </aside>
          </div>
        )}
      </div>
    </div>
  );
}

interface TabButtonProps {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}

function TabButton({ active, onClick, icon, label }: TabButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-2 px-4 py-2.5 rounded-t-lg font-medium text-sm transition-colors',
        active
          ? 'bg-neutral-100 dark:bg-neutral-800 text-orange-600 dark:text-orange-400 border-b-2 border-orange-500'
          : 'text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 hover:bg-neutral-100 dark:hover:bg-neutral-800'
      )}
    >
      {icon}
      {label}
    </button>
  );
}

export default BatchImageMode;
